/**
 * SRES · CinemaOnboardingCoach (Iter113 · Feb 2026).
 *
 * One-time coach mark that teaches the header eye-slash + Ctrl/⌘ +
 * Shift + \\ shortcut. Rendered on the very first wizard visit AFTER
 * the layout is fully painted, then dismisses itself and never
 * appears again for that browser profile.
 *
 * Anchoring
 * ---------
 * Positions itself against `[data-testid=header-cinema-toggle]` using
 * `getBoundingClientRect`. Falls back to the top-right corner if the
 * button isn't in the DOM yet (still shows the tip; no arrow).
 *
 * Dismissal
 * ---------
 *   · Tap "Got it" — persist dismissal.
 *   · Click outside the tip — persist dismissal.
 *   · Press Esc — persist dismissal.
 *   · Auto-timeout at 14 seconds — persist dismissal.
 *
 * Storage
 * -------
 *   localStorage["sres.cinema-onboarding-shown"] · "1"
 */
import { useEffect, useRef, useState } from "react";
import { EyeSlash, X, Keyboard } from "@phosphor-icons/react";

const LS_KEY = "sres.cinema-onboarding-shown";
const AUTO_DISMISS_MS = 14_000;


function readShown() {
  try { return localStorage.getItem(LS_KEY) === "1"; } catch { return true; }
}

function markShown() {
  try { localStorage.setItem(LS_KEY, "1"); } catch { /* silent */ }
}


export default function CinemaOnboardingCoach() {
  const [visible, setVisible] = useState(false);
  const [anchor, setAnchor]   = useState(null);
  const timeoutRef = useRef(null);
  const cardRef    = useRef(null);

  // Mount once — with a short delay so the header has painted.
  useEffect(() => {
    if (readShown()) return;
    const timer = setTimeout(() => {
      // If the header is currently hidden (cinema mode already on)
      // there is nothing to point at — mark shown and bail.
      const btn = document.querySelector("[data-testid=header-cinema-toggle]");
      if (!btn) {
        // Header isn't mounted yet (e.g. cinema mode already on).
        // Try again in a few seconds; if still no button, silently skip.
        const retry = setTimeout(() => {
          const btn2 = document.querySelector("[data-testid=header-cinema-toggle]");
          if (btn2) {
            setAnchor(btn2.getBoundingClientRect());
            setVisible(true);
          } else {
            markShown();
          }
        }, 2400);
        return () => clearTimeout(retry);
      }
      setAnchor(btn.getBoundingClientRect());
      setVisible(true);
    }, 900);
    return () => clearTimeout(timer);
  }, []);

  // Reposition on resize / scroll so the coach stays glued to the
  // button as the user pans around before dismissing.
  useEffect(() => {
    if (!visible) return () => {};
    const reposition = () => {
      const btn = document.querySelector("[data-testid=header-cinema-toggle]");
      if (!btn) return;
      setAnchor(btn.getBoundingClientRect());
    };
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [visible]);

  // Auto-dismiss + Esc + click-outside.
  useEffect(() => {
    if (!visible) return () => {};
    timeoutRef.current = setTimeout(() => dismiss(), AUTO_DISMISS_MS);
    const onKey  = (e) => { if (e.key === "Escape") dismiss(); };
    const onDoc  = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) dismiss();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDoc);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [visible]);

  const dismiss = () => { markShown(); setVisible(false); };

  if (!visible) return null;

  // Position the tip just below the button, right-aligned to it.
  const style = anchor
    ? {
        top:   `${anchor.bottom + 8}px`,
        right: `${Math.max(8, window.innerWidth - anchor.right)}px`,
      }
    : { top: "70px", right: "12px" };

  return (
    <>
      {/* Soft spotlight arrow */}
      {anchor && (
        <div
          className="fixed z-[110] pointer-events-none"
          style={{
            top:  `${anchor.bottom + 2}px`,
            left: `${anchor.left + anchor.width / 2 - 6}px`,
          }}
          aria-hidden>
          <div className="w-3 h-3 bg-slate-900 rotate-45 shadow-lg" />
        </div>
      )}

      <div
        ref={cardRef}
        role="dialog"
        aria-labelledby="sres-cinema-coach-title"
        data-testid="cinema-onboarding-coach"
        className="fixed z-[111] w-[300px] max-w-[calc(100vw-16px)] rounded-lg
                        bg-slate-900 text-white shadow-2xl ring-1 ring-slate-700/50
                        p-3.5 animate-in fade-in slide-in-from-top-2"
        style={style}>
        <div className="flex items-start gap-2 mb-2">
          <div className="w-7 h-7 rounded-md bg-brand-blue/20 text-brand-blue
                              flex items-center justify-center shrink-0">
            <EyeSlash size={15} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <div id="sres-cinema-coach-title"
                     className="text-[13px] font-semibold leading-tight">
              Cinema mode
            </div>
            <div className="text-[11.5px] text-slate-300 mt-0.5">
              Reclaim every pixel for the 3D cockpit
            </div>
          </div>
          <button type="button"
                      onClick={dismiss}
                      aria-label="Dismiss tip"
                      data-testid="cinema-onboarding-close"
                      className="w-6 h-6 rounded text-slate-400 hover:text-white
                                    hover:bg-white/10 flex items-center justify-center transition">
            <X size={12} weight="bold" />
          </button>
        </div>
        <p className="text-[11.5px] text-slate-300 leading-snug">
          Tap the <span className="inline-flex items-baseline gap-0.5 px-1 rounded
                                              bg-slate-800 ring-1 ring-slate-700 mx-0.5
                                              text-slate-100">
            <EyeSlash size={10} weight="bold" className="translate-y-[1px]" />
          </span>
          button in the top bar to hide the sidebar AND top nav.
          The main pane fills your whole screen — perfect for 3D
          block models, GIS maps and CPR PDF previews.
        </p>
        <div className="mt-2.5 flex items-center gap-2 rounded-md
                              bg-slate-800 ring-1 ring-slate-700 px-2.5 py-1.5">
          <Keyboard size={13} className="text-slate-400 shrink-0" />
          <div className="text-[11px] text-slate-300 flex-1">
            Power shortcut ·
            <span className="ml-1 font-mono text-slate-100 tracking-wide">
              ⌘ / Ctrl + Shift + \
            </span>
          </div>
        </div>
        <button type="button"
                    onClick={dismiss}
                    data-testid="cinema-onboarding-got-it"
                    className="mt-3 w-full h-8 rounded-md bg-brand-blue hover:opacity-90
                                  text-white text-[12px] font-semibold transition">
          Got it
        </button>
      </div>
    </>
  );
}
